import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Gavel } from "lucide-react";
import {
  Button,
  DetailSection,
  EmptyState,
  Input,
  ListCard,
  SkeletonList,
  SkeletonText,
  StatusBadge,
  Textarea,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency, isoDateInTz } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import {
  tpsCollectionsApi,
  type TpsCollectionCase,
  type TpsCollectionCaseDetail,
} from "../api/tpsCollections";

// TPS Servicing Portal PR-H — the firm's "Collections" surface. Lists the
// default/collection cases assigned to this firm (DefaultCase.TpsId scoped) and
// lets it add notes and record recoveries; recording a recovery books the
// firm's collection-recovery fee (record-only). Status transitions, plans, and
// legal actions stay staff-only in v1. No React Query in the portal — load is
// plain useState and the actions re-run the loaders.

// Mirrors the staff record-recovery source vocabulary (free string server-side).
const RECOVERY_SOURCES = [
  "borrower-post-default",
  "guarantor",
  "collateral-sale",
  "insurance",
  "DACA-sweep",
  "judgment",
  "settlement",
  "deficiency",
  "other",
] as const;

function humanize(value: string): string {
  return value.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusTone(status: string): StatusTone {
  if (status === "closed-final" || status === "resolved-recovered" || status === "cured-restored")
    return "ok";
  if (status === "charged-off") return "danger";
  if (status === "default-declared" || status === "notice-of-default-sent") return "warn";
  return "progress";
}

function feeRateLabel(rate: number): string {
  return `${(rate * 100).toFixed(rate * 100 % 1 === 0 ? 0 : 2)}%`;
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function TpsCollectionsTab() {
  const [rows, setRows] = useState<TpsCollectionCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openCase = params.get("case");

  const loadList = useCallback(async () => {
    const data = await tpsCollectionsApi.list();
    setRows(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsCollectionsApi.list();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your collection cases."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpenCase = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("case", id);
    else updated.delete("case");
    setParams(updated, { replace: true });
  };

  if (openCase) {
    return (
      <CaseDetail caseId={openCase} onBack={() => setOpenCase(null)} refreshList={loadList} />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  if (!rows) {
    return <SkeletonList rows={4} data-testid="tps-collections-loading" />;
  }

  return (
    <section aria-label="Collections" className="flex flex-col gap-space-4">
      {rows.length === 0 ? (
        <EmptyState
          icon={Gavel}
          title="No collection cases yet"
          description="When Phoenix escalates a defaulted loan to collections, it appears here for you to work — record recoveries and you earn your agreed fee on amounts collected."
        />
      ) : (
        <div className="flex flex-col gap-space-3">
          {rows.map((c) => (
            <ListCard
              key={c.id}
              data-testid={`tps-collection-row-${c.id}`}
              title={`${c.loanNumber || "—"}${c.borrowerName ? ` — ${c.borrowerName}` : ""}`}
              status={<StatusBadge tone={statusTone(c.status)}>{humanize(c.status)}</StatusBadge>}
              meta={
                <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
                  {c.originalExposure != null && (
                    <>
                      <span>{formatCurrency(c.originalExposure)} exposure</span>
                      <span aria-hidden>·</span>
                    </>
                  )}
                  <span>{formatCurrency(c.grossRecovered)} recovered</span>
                  <span aria-hidden>·</span>
                  <span>
                    Opened <Timestamp value={c.triggerDate} />
                  </span>
                </span>
              }
              actions={
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setOpenCase(c.id)}
                  data-testid={`tps-collection-open-${c.id}`}
                >
                  Work case
                </Button>
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CaseDetail({
  caseId,
  onBack,
  refreshList,
}: {
  caseId: string;
  onBack: () => void;
  refreshList: () => Promise<unknown>;
}) {
  const [detail, setDetail] = useState<TpsCollectionCaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    const data = await tpsCollectionsApi.get(caseId);
    setDetail(data);
    return data;
  }, [caseId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsCollectionsApi.get(caseId);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load this case."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const backButton = (
    <Button type="button" variant="text" size="sm" onClick={onBack} data-testid="tps-collection-back">
      <ArrowLeft size={16} aria-hidden />
      Back to collections
    </Button>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <DetailSection>
          <SkeletonText lines={6} />
        </DetailSection>
      </div>
    );
  }

  return (
    <section
      aria-label={`Collection case for ${detail.loanNumber}`}
      className="flex flex-col gap-space-4"
    >
      <div className="flex items-center justify-between gap-space-3">
        {backButton}
        <StatusBadge tone={statusTone(detail.status)}>{humanize(detail.status)}</StatusBadge>
      </div>

      <div>
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">
          {detail.loanNumber || "—"}
          {detail.borrowerName ? ` — ${detail.borrowerName}` : ""}
        </h2>
        <p className="mt-space-1 text-small text-gray-mid">
          {detail.triggeredBy ? `${humanize(detail.triggeredBy)} · ` : ""}Opened{" "}
          <Timestamp value={detail.triggerDate} />
        </p>
        {detail.description && (
          <p className="mt-space-2 text-small text-onyx">{detail.description}</p>
        )}
      </div>

      <DetailSection title="Recovery summary">
        <dl className="grid grid-cols-2 gap-x-space-4 gap-y-space-2 text-small">
          {detail.originalExposure != null && (
            <Line label="Original exposure" value={formatCurrency(detail.originalExposure)} />
          )}
          <Line label="Gross recovered" value={formatCurrency(detail.grossRecovered)} />
          <Line label="Net recovered" value={formatCurrency(detail.netRecovered)} />
          <Line label="Your fee rate" value={feeRateLabel(detail.feeRate)} />
          <Line label="Fee earned" value={formatCurrency(detail.feeEarnedTotal)} strong />
        </dl>
      </DetailSection>

      <RecordRecoveryForm caseId={caseId} onRecorded={loadDetail} refreshList={refreshList} />

      <DetailSection title="Recoveries">
        {detail.recoveries.length === 0 ? (
          <p className="text-small text-gray-mid">No recoveries recorded yet.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-space-2 p-0">
            {detail.recoveries.map((r) => (
              <li
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-space-2 rounded-md border border-border-light p-space-3 text-small"
                data-testid={`tps-collection-recovery-${r.id}`}
              >
                <span className="flex flex-col">
                  <span className="font-semibold text-onyx">{humanize(r.recoverySource)}</span>
                  <span className="text-gray-mid">
                    <Timestamp value={r.recoveryDate} /> · net {formatCurrency(r.netAmount)}
                  </span>
                </span>
                <span className="flex flex-col text-right">
                  <span className="font-semibold text-onyx">{formatCurrency(r.grossAmount)}</span>
                  <span className="text-gray-mid">{formatCurrency(r.feeEarned)} fee</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </DetailSection>

      <AddNoteForm caseId={caseId} onAdded={loadDetail} />

      <DetailSection title="Activity">
        {detail.timeline.length === 0 ? (
          <p className="text-small text-gray-mid">No activity yet.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-space-2 p-0">
            {detail.timeline.map((e) => (
              <li key={e.id} className="text-small">
                <span className="text-gray-mid">
                  <Timestamp value={e.eventDate} />
                </span>{" "}
                <span className="text-onyx">{e.description}</span>
              </li>
            ))}
          </ul>
        )}
      </DetailSection>
    </section>
  );
}

function RecordRecoveryForm({
  caseId,
  onRecorded,
  refreshList,
}: {
  caseId: string;
  onRecorded: () => Promise<unknown>;
  refreshList: () => Promise<unknown>;
}) {
  const toast = useToast();
  // The portal has no per-viewer timezone yet, so this resolves to the app's
  // zone — what every other date on this surface already shows. It stops
  // being the UTC day, which was a day ahead every evening.
  const today = isoDateInTz(new Date());
  const [recoveryDate, setRecoveryDate] = useState(today);
  const [recoverySource, setRecoverySource] = useState<string>(RECOVERY_SOURCES[0]);
  const [grossAmount, setGrossAmount] = useState("");
  const [recoveryCosts, setRecoveryCosts] = useState("0");
  const [depositAccount, setDepositAccount] = useState("");

  const gross = Number(grossAmount);
  const costs = Number(recoveryCosts);
  const valid =
    Number.isFinite(gross) && gross > 0 &&
    Number.isFinite(costs) && costs >= 0 &&
    depositAccount.trim().length > 0;

  const record = useSubmittingMutation(
    () =>
      tpsCollectionsApi.recordRecovery(caseId, {
        recoveryDate,
        recoverySource,
        grossAmount: gross,
        recoveryCosts: costs,
        depositAccount: depositAccount.trim(),
      }),
    {
      refresh: [onRecorded, refreshList],
      onSuccess: () => {
        setGrossAmount("");
        setRecoveryCosts("0");
        setDepositAccount("");
        toast.success("Recovery recorded.");
      },
      onError: (err) => toast.error(errorMessage(err, "Couldn't record the recovery. Try again.")),
    },
  );

  return (
    <DetailSection title="Record a recovery">
      <div className="flex flex-col gap-space-3">
        <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
          <label className="flex flex-col gap-space-1">
            <span className="text-caption uppercase tracking-label text-gray-mid">Date</span>
            <Input
              type="date"
              value={recoveryDate}
              onChange={(e) => setRecoveryDate(e.target.value)}
              disabled={record.pending}
              data-testid="tps-collection-recovery-date"
            />
          </label>
          <label className="flex flex-col gap-space-1">
            <span className="text-caption uppercase tracking-label text-gray-mid">Source</span>
            <select
              className="h-10 rounded-md border border-border-light bg-white-pure px-space-2 text-small text-onyx disabled:bg-white-warm disabled:opacity-60"
              value={recoverySource}
              onChange={(e) => setRecoverySource(e.target.value)}
              disabled={record.pending}
              data-testid="tps-collection-recovery-source"
            >
              {RECOVERY_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-space-1">
            <span className="text-caption uppercase tracking-label text-gray-mid">
              Gross amount collected
            </span>
            <Input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={grossAmount}
              onChange={(e) => setGrossAmount(e.target.value)}
              disabled={record.pending}
              invalid={grossAmount.length > 0 && !(gross > 0)}
              data-testid="tps-collection-recovery-gross"
            />
          </label>
          <label className="flex flex-col gap-space-1">
            <span className="text-caption uppercase tracking-label text-gray-mid">
              Recovery costs
            </span>
            <Input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={recoveryCosts}
              onChange={(e) => setRecoveryCosts(e.target.value)}
              disabled={record.pending}
              data-testid="tps-collection-recovery-costs"
            />
          </label>
          <label className="flex flex-col gap-space-1 sm:col-span-2">
            <span className="text-caption uppercase tracking-label text-gray-mid">
              Deposit account
            </span>
            <Input
              type="text"
              value={depositAccount}
              onChange={(e) => setDepositAccount(e.target.value)}
              disabled={record.pending}
              placeholder="Where the funds were deposited"
              data-testid="tps-collection-recovery-account"
            />
          </label>
        </div>
        <div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void record.submit()}
            disabled={record.pending || !valid}
            loading={record.pending}
            data-testid="tps-collection-recovery-submit"
          >
            Record recovery
          </Button>
        </div>
      </div>
    </DetailSection>
  );
}

function AddNoteForm({ caseId, onAdded }: { caseId: string; onAdded: () => Promise<unknown> }) {
  const toast = useToast();
  const [note, setNote] = useState("");

  const add = useSubmittingMutation(() => tpsCollectionsApi.addNote(caseId, note.trim()), {
    refresh: onAdded,
    onSuccess: () => {
      setNote("");
      toast.success("Note added.");
    },
    onError: (err) => toast.error(errorMessage(err, "Couldn't add the note. Try again.")),
  });

  return (
    <DetailSection title="Add a note">
      <div className="flex flex-col gap-space-2">
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          disabled={add.pending}
          aria-label="Add a note"
          placeholder="Record an update, a borrower contact, or a next step."
          data-testid="tps-collection-note"
        />
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void add.submit()}
            disabled={add.pending || note.trim().length === 0}
            loading={add.pending}
            data-testid="tps-collection-note-submit"
          >
            Add note
          </Button>
        </div>
      </div>
    </DetailSection>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <>
      <dt className={strong ? "font-semibold text-onyx" : "text-gray-mid"}>{label}</dt>
      <dd className={`m-0 text-right ${strong ? "font-semibold text-onyx" : "text-onyx"}`}>
        {value}
      </dd>
    </>
  );
}
